/**
 * @file sign-in-form.tsx
 * @description Shared email/password sign-in form for the staff, provider-portal
 *              and group-portal sign-in pages.
 * @author William Donaldson
 * @last_modified 2026-08-04
 *
 * WHY THE COPY IS HARDCODED HERE AND NOT PROPS: none of the visible text varies
 * between the three surfaces — all three render the same title, labels and
 * placeholders. Making copy configurable would create three call sites where a
 * typo can silently change wording that today cannot diverge. The things that
 * genuinely differ are each an explicit prop below.
 */
"use client";

import { Lock, Mail } from "lucide-react";
import Link from "next/link";
import { useActionState, useEffect, useRef, useState } from "react";
import { useFormStatus } from "react-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

import { useWarmupRetry } from "@/hooks/use-warmup-retry";
import { FORM_ERROR_CODES, type FormActionState } from "@/server/actions/types";

function SubmitButton({ isRetrying }: { isRetrying: boolean }) {
  const { pending } = useFormStatus();
  // WHY pending || isRetrying: During warmup retries, `pending` goes false
  // briefly between the error return and the next requestSubmit(). isRetrying
  // bridges that gap so the user sees one continuous loading spinner.
  return (
    <Button type="submit" className="w-full" loading={pending || isRetrying}>
      Sign In
    </Button>
  );
}

interface SignInFormProps {
  searchParams: { error?: string; success?: string; redirect?: string };
  /** The surface's own Server Action. */
  action: (
    prevState: FormActionState,
    formData: FormData
  ) => Promise<FormActionState>;
  /** Lost-password destination, including the surface's `?from=` parameter. */
  lostPasswordHref: string;
  /**
   * Email autocomplete token for this surface.
   *
   * WHY A PROP AND NOT A CONSTANT: staff uses "off" while both portals use
   * "email". Normalizing them would change browser autofill behavior on a
   * login screen, so the difference is carried explicitly rather than erased.
   */
  emailAutoComplete: "off" | "email";
  /**
   * Renders the "Remember me" checkbox.
   *
   * WHY STAFF ONLY: the posted `remember` field is what selects a 30-day
   * session instead of an 8-hour one. The portal actions hardcode
   * `rememberMe: false`, so rendering it there would be a control that
   * silently does nothing.
   */
  showRememberMe?: boolean;
  /** Renders the "Don't have an account? Create one" line (staff only — portal
   *  and group users are invited, never self-registered). */
  showRegistrationLink?: boolean;
  /**
   * Optional dev-only warmup action, injected by the route rather than imported
   * here so the route's own action module stays the boundary that tests mock.
   */
  preWarmAction?: () => Promise<void>;
}

export function SignInForm({
  searchParams,
  action,
  lostPasswordHref,
  emailAutoComplete,
  showRememberMe,
  showRegistrationLink,
  preWarmAction,
}: SignInFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  // WHY CONTROLLED: React 19 resets uncontrolled form inputs after a
  // useActionState action completes. The email survives via defaultValue
  // from state.values, but the password gets wiped. A controlled input
  // preserves the value across resets so warmup retries can resubmit it.
  const [password, setPassword] = useState("");

  const [state, formAction] = useActionState(action, {
    ok: true,
    formError: null,
    fieldErrors: {},
  });

  const { isRetrying } = useWarmupRetry(formRef, state);

  // WHY FIRE-AND-FORGET: Warms the server action pipeline on cold dev starts
  // so the sign-in action is more likely to succeed on first submit. Does NOT
  // gate the button — if the warmup hangs or fails, the action's own cookies()
  // try/catch + useWarmupRetry auto-retry provide the fallback.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    preWarmAction?.().catch(() => {});
  }, [preWarmAction]);

  const successMessage =
    searchParams.success === "password-reset"
      ? "Password reset successfully. Please sign in."
      : null;

  // WHY SUPPRESS WARMUP ERRORS: The user should never see infrastructure
  // errors from cold starts. During retries, isRetrying hides everything.
  // If retries exhaust (extremely rare), show a connection message instead
  // of the raw "Server is warming up" text.
  const visibleError = (() => {
    if (!state.formError || isRetrying) return null;
    if (state.errorCode === FORM_ERROR_CODES.SERVER_WARMUP) {
      return "Connection issue — please refresh the page and try again.";
    }
    return state.formError;
  })();

  return (
    <>
      {successMessage && (
        <Alert className="mb-4">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
        </CardHeader>
        {/* WHY noValidate: Native browser validation blocks requestSubmit()
            during warmup retries. Zod server-side validation catches all the
            same constraints and returns inline field errors instead. */}
        <form ref={formRef} action={formAction} noValidate>
          {/* WHY HIDDEN INPUT: Pass redirect URL through the form so the server
              action can redirect to the correct page after successful signin */}
          {searchParams.redirect && (
            <input type="hidden" name="redirect" value={searchParams.redirect} />
          )}

          <CardContent className="space-y-4">
            {visibleError && (
              <Alert variant="destructive">
                <AlertDescription>{visibleError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Your email</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                </span>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="example@company.com"
                  required
                  autoComplete={emailAutoComplete}
                  defaultValue={state.values?.email ?? ""}
                  error={!!state.fieldErrors?.email}
                  className="pl-10"
                />
              </div>
              {state.fieldErrors?.email && (
                <p className="text-sm text-destructive">
                  {state.fieldErrors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground">
                  <Lock className="h-4 w-4" />
                </span>
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="Password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!state.fieldErrors?.password}
                  className="pl-10"
                />
              </div>
              {state.fieldErrors?.password && (
                <p className="text-sm text-destructive">
                  {state.fieldErrors.password}
                </p>
              )}
            </div>

            <div
              className={
                showRememberMe
                  ? "flex items-center justify-between"
                  : "flex items-center justify-end"
              }
            >
              {showRememberMe && (
                <div className="flex items-center gap-2">
                  <Checkbox id="remember" name="remember" />
                  <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                    Remember me
                  </Label>
                </div>
              )}
              <Link
                href={lostPasswordHref}
                className="text-sm text-primary hover:underline"
              >
                Lost password?
              </Link>
            </div>
          </CardContent>
          {showRegistrationLink ? (
            <CardFooter className="flex-col gap-4">
              <SubmitButton isRetrying={isRetrying} />
              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link
                  href="/create-organization"
                  className="font-medium text-primary hover:underline"
                >
                  Create one
                </Link>
              </p>
            </CardFooter>
          ) : (
            <CardFooter>
              <SubmitButton isRetrying={isRetrying} />
            </CardFooter>
          )}
        </form>
      </Card>
    </>
  );
}
